import React, { useCallback, useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Col, Row } from 'react-bootstrap';
import { ICategoryArticle } from "../../helpers/interfaces/ranking";
import Sidebar from "../../components/Sidebar";
import CustomPagination from "../../common/Pagination";
import CssLoader from '../../components/cssloader/CssLoader';
import { categoryService } from "../../services/CategoryService";
import { FacebookIcon, TwitterIcon, WhatsAppIcon } from "../../components/Icons";
import { Article } from "../Categorie/Categoria/Article";
import { matchesService } from "../../services/MatchesService";
import { useMenuSingleton } from '../../singleton/menuSingleton';
import Advertising from "../../common/Advertising";
import PageMeta from '../../components/PageMeta';
import './Articolo.css';

export default function ArticoloPage() {
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const { getMenu, getSlugPath } = useMenuSingleton();
    const { id } = useParams<{ id: string }>();
    const [articleData, setArticleData] = useState<ICategoryArticle | null>(null);
    const [currentArticles, setCurrentArticles] = useState<ICategoryArticle[]>([]);
    const [currentPage, setCurrentPage] = useState<number>(1);
    const [articles, setArticles] = useState<ICategoryArticle[]>([]);
    const pageLimit = 8;

    const fetchArticles = useCallback(async (categoryId: number, articleIdToRemove?: string) => {
        const articles = await categoryService.getCategoryArticles(categoryId);
        if (articles && typeof articles !== "string") {
            const filteredArticles = articleIdToRemove
                ? articles.ultimiPerCategoria.filter(
                    // eslint-disable-next-line eqeqeq
                    (article) => article.id != Number(articleIdToRemove)
                )
                : articles.ultimiPerCategoria;

            setArticles(filteredArticles);
            setCurrentArticles(filteredArticles.slice(0, pageLimit));
        }
    }, [pageLimit]);

    const fetchArticleData = useCallback(async (articleId: string) => {
        setIsLoading(true);

        window.scrollTo({
            top: 0,
            left: 0,
            behavior: "smooth"
        });

        try {
            const data = await matchesService.getArticleData(articleId)
            if (data) {
                setArticleData(data);
                await fetchArticles(data.idCategoria, articleId);
            }
        } catch (error) {
            console.error("Error fetching article data: ", error);
        } finally {
            setIsLoading(false);
        }
    }, [fetchArticles]);

    useEffect(() => {
        if (id) {
            fetchArticleData(id);
        }
    }, [fetchArticleData, id]);

    const onPageChange = (pageNumber: number) => {
        setCurrentPage(pageNumber);
        const start = (pageNumber - 1) * pageLimit;
        const end = start + pageLimit;
        setCurrentArticles(articles.slice(start, end));
    };

    const handlePrint = () => {
        window.print();
    };

    const slugPath = getSlugPath(getMenu().data);
    const categoryPath = slugPath["_articoli_" + articleData?.idCategoria];

    const params = new URLSearchParams(window.location.search);
    const isBot = params.get("bot") === "whatsapp";

    if (isBot && articleData) {
        return (
            <>
                <PageMeta
                    title={articleData.titolo}
                    description={articleData.articoloBreve}
                    image={articleData.img1}
                    url={document.location.href}
                    type="article"
                />
            </>
        );
    }

    return (
        <React.Fragment>
            <PageMeta
                title={articleData?.titolo ? `${articleData.titolo} – Calcio a 5 Live` : "Calcio a 5 Live"}
                description={articleData?.articoloBreve}
                image={articleData?.img1}
                url={document.location.href}
                type={"article"}
            />
            <Row id="page_content">
                <Col id="content" xs={12} lg={9}>
                    <h1>{articleData?.titolo}</h1>
                    <div className="article-center">
                        <div className="article-header">
                            {articleData?.img1 && (
                                <div className="image-container">
                                    <img className="article-image" src={articleData.img1} alt={articleData?.titolo} />
                                </div>
                            )}

                            <div className="title-share">
                                <div className="title-container">
                                    <div className="article-info">
                                        <p>
                                            <span>{articleData?.dataPubblicazioneData} - </span>
                                            <span className="author-name">{articleData?.nomeAutore}</span>
                                        </p>
                                        <p>Categoria: <Link to={categoryPath}>{articleData?.nomeCategoria}</Link></p>
                                        <p>Società: <Link to={`/societa/${articleData?.idSocieta}`}>{articleData?.nomeSocieta}</Link></p>
                                        <p>Visualizzazioni: {articleData?.letture}</p>
                                    </div>
                                </div>

                                <div className="share-to-social-media">
                                    <div className="social-media-actions">
                                        <a className="social-media-svg" href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(document.location.href)}`} target="_blank" title="Facebook" rel="noopener noreferrer">
                                            <FacebookIcon />
                                        </a>
                                        <a className="social-media-svg" href={`https://api.whatsapp.com/send?text=${encodeURIComponent(document.location.href)}`} target="_blank" title="WhatsApp" rel="noopener noreferrer">
                                            <WhatsAppIcon />
                                        </a>
                                        <a className="social-media-svg" href={`https://twitter.com/share?url=${encodeURIComponent(document.location.href)}&text=${encodeURIComponent(articleData?.titolo ?? '')}`} target="_blank" title="Twitter" rel="noopener noreferrer">
                                            <TwitterIcon />
                                        </a>
                                        <div className="social-media-print" onClick={handlePrint}>Stampa l’articolo</div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="article-content">
                            <div dangerouslySetInnerHTML={{ __html: articleData?.articolo || '' }} />
                        </div>

                        {articleData && articleData.infosocieta && (
                            (() => {
                                const sponsorList = [
                                    {
                                        img: articleData.infosocieta.sponsor1Img,
                                        url: articleData.infosocieta.sponsor1Url,
                                    },
                                    {
                                        img: articleData.infosocieta.sponsor2Img,
                                        url: articleData.infosocieta.sponsor2Url,
                                    },
                                    {
                                        img: articleData.infosocieta.sponsor3Img,
                                        url: articleData.infosocieta.sponsor3Url,
                                    },
                                    {
                                        img: articleData.infosocieta.sponsor4Img,
                                        url: articleData.infosocieta.sponsor4Url,
                                    },
                                    {
                                        img: articleData.infosocieta.sponsor5Img,
                                        url: articleData.infosocieta.sponsor5Url,
                                    },
                                    {
                                        img: articleData.infosocieta.sponsor6Img,
                                        url: articleData.infosocieta.sponsor6Url,
                                    },
                                ];

                                const hasAtLeastOneImage = sponsorList.some(
                                    (s) => s.img && s.img.trim() !== ""
                                );

                                return hasAtLeastOneImage ? (
                                    <div className="team-sponsors">
                                        <h2>Sponsor</h2>
                                        <hr />
                                        <div className="sponsor-logos d-flex flex-wrap">
                                            {sponsorList.map((s, index) =>
                                                s.img && s.img.trim() !== "" ? (
                                                    <a
                                                        key={index}
                                                        href={s.url && s.url.trim() !== "" ? s.url : "#"}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className="me-2 mb-2 d-inline-block"
                                                    >
                                                        <img
                                                            src={s.img}
                                                            alt={`Sponsor ${index + 1}`}
                                                        />
                                                    </a>
                                                ) : null
                                            )}
                                        </div>
                                    </div>
                                ) : null;
                            })()
                        )}
                    </div>

                    <div className="related-articles">
                        <hr />
                        <Advertising code="new_banner_articolo_1" as="div" className='text-center py-4' />
                        <h2 className="mt-2 mb-5">Potrebbe interessarti anche...</h2>
                        {
                            currentArticles.length > 0 ? currentArticles.map((article, index) => (
                                <React.Fragment key={article.id}>
                                    {index !== 0 && index % 4 === 0 && (
                                        <Advertising code="new_banner_articolo_2" as="div" className='text-center py-4' />
                                    )}
                                    <Article article={article} />
                                </React.Fragment>
                            )) : <Row className="pb-3 d-flex justify-content-center">Nessuna informazione trovata</Row>
                        }
                        <div className="d-flex justify-content-center">
                            <CustomPagination
                                totalRecords={articles.length}
                                pageLimit={pageLimit}
                                pageNeighbours={2}
                                onPageChanged={onPageChange}
                                currentPage={currentPage}
                            />
                        </div>
                    </div>
                </Col>
                <Col id="sidebar" xs={12} lg={3} className="pt-3">
                    <Sidebar />
                </Col>
            </Row>

            <CssLoader active={isLoading} text="Caricamento dell'articolo in corso..." />
        </React.Fragment >
    );
}
